import { useState, useRef } from "react";
import { message, Modal } from "antd";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { getOAuthHeaders } from "../../../../constants/oAuthValidation";
import { timeStamp, timeValidator, currentDay, dateValidator } from "../../../../utils/clock";
import dayjs from "dayjs";
import db from "../../../../database";
import { pricingRuleController } from "../PricingRules/pricingRuleController";
import { discardCouponService, verifyCouponsService, externalCoupon } from "../../../../services";
import { logCouponValidation } from "../../../../utils/dataIntegrity";
import upsertPOSLog from "../../Retail/posLog";
import messages from "../../../../errors/messages.en-IN.json";
import useCartStore from "../../../../stores/useCartStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import { roundTo, applyCurrencyRounding, getRoundingMethod } from "../../../../utils/tillHelpers";
import { findPricingRuleById } from "../../../../utils/pricingRulesCache";
import { groupItemsByTaxRate } from "../utils/taxCalculations";
import { getIsInterState } from "../../../../utils/taxUtils";

const useCouponProcessing = ({
  cart,
  setCart,
  tillData,
  precision,
  posConfig,
  orderType,
  cartRef,
  isExchange,
  taxIncludeFlag,
  processBillDiscounts,
  removeAllDiscounts,
  searchInputRef,
  setIsInputFocused,
  selectedSaleTypeData,
  defaultCustomer,
  openPaymentModalByCustomer,
  discountApproval,
  totalBillDiscountData,
  selectedExternalCouponType,
  additionalInfoForm,
}) => {
  const { t } = useTranslation();

  const [couponModalVisible, setCouponModalVisible] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [couponVerificationData, setCouponVerificationData] = useState(null);
  const [showCouponApproval, setShowCouponApproval] = useState(false);
  const [couponApprovals, setCouponApprovals] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [totalBillCouponData, setTotalBillCouponData] = useState({});
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState([]);
  const [couponValidating, setCouponValidating] = useState(false);
  const isValidatingRef = useRef(false);
  const couponInputRef = useRef(null);

  const closeCouponModal = () => {
    setCouponModalVisible(false);
    setSelectedCoupon(null);
    setCouponInput("");
    setIsInputFocused(false);
    setAdditionalInfo([]);
    if (searchInputRef?.current) {
      searchInputRef?.current?.focus();
    }
  };

  const handleCouponInput = (value) => {
    if (value === "clear") {
      setCouponInput("");
    } else if (value === "x") {
      setCouponInput(`${couponInput.toString().substring(0, couponInput.toString().length - 1)}`);
    } else {
      setCouponInput(`${couponInput}${value}`);
    }
  };

  const toCamelCase = (str) => {
    if (!str) return "";
    return str
      .replace(/[^a-zA-Z0-9 ]/g, "")
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => (index === 0 ? word.toLowerCase() : word.toUpperCase()))
      .replace(/\s+/g, "");
  };

  const processCouponApplication = async (verifyCoupons, pricingRules, uniqReferenceId) => {
    const couponData = Array.isArray(verifyCoupons) ? verifyCoupons[0] : verifyCoupons;
    pricingRules = Array.isArray(pricingRules) ? pricingRules : [pricingRules];
    let approvalFlag = true;
    let discountAmt = 0;
    let cartObj = cart;
    const pricingRule = pricingRules[0];
    cartObj.items.map((item) => {
      let excludingFlag = true;
      const hasApplicableProducts = Array.isArray(pricingRule?.applicableProducts) && pricingRule.applicableProducts.length > 0;

      const isEligibleProduct = hasApplicableProducts ? pricingRule.applicableProducts.some((p) => p?._id === item?.productId) : true;

      if (!isEligibleProduct) {
        return;
      }
      const matchingCategory = pricingRules[0]?.mPricingPcategories?.find((pc) => pc.mProductCategoryId === item.mProductCategoryId);
      if (
        (pricingRules[0].excludeProductCategories === "Y" && matchingCategory) ||
        (pricingRules[0].excludeProductCategories !== "Y" && !matchingCategory && pricingRules[0].mPricingPcategories.length > 0)
      ) {
        excludingFlag = false;
      }

      const matchingBrand = pricingRules[0]?.mPricingBrands?.find((pc) => pc.mBrandId === item.productBrandId);
      if ((pricingRules[0].excludeBrands === "Y" && matchingBrand) || (pricingRules[0].excludeBrands !== "Y" && !matchingBrand && pricingRules[0].mPricingBrands.length > 0)) {
        excludingFlag = false;
      }

      const matchingProductIndex = pricingRules[0]?.mPricingXProducts?.findIndex((op) => op.mProductId === item.productId);
      if (
        (pricingRules[0].excludeProducts === "Y" && matchingProductIndex !== -1) ||
        (pricingRules[0].excludeProducts !== "Y" && matchingProductIndex === -1 && pricingRules[0].mPricingXProducts.length > 0)
      ) {
        excludingFlag = false;
      }

      let pricingRuleFlag = true;

      if (item.discount <= 0 && item.mPricingRulesId !== null && item.mPricingRulesId !== undefined) {
        pricingRules[0].mPricingXProducts.forEach((res) => {
          if (res.mProductId === item.productId) {
            delete item.priority;
          }
        });
      }
      const saleTypeValidation =
        pricingRules[0]?.cwrSaletypeId === null || orderType === undefined ? true : pricingRules[0]?.cwrSaletypeId === orderType?.cwrSaletype?.cwrSaletypeId;
      let addToCart = cart.items.filter((item) => pricingRules[0]?.mPricingXProducts?.some((pro) => item.productId === pro.mProductId));
      // let iscoupon = true;
      if (
        saleTypeValidation &&
        excludingFlag &&
        pricingRuleFlag &&
        (!Array.isArray(pricingRule?.applicableProducts) ||
          pricingRule.applicableProducts.length === 0 ||
          pricingRule.applicableProducts.some((p) => p?._id === item?.productId)) &&
        (item?.priority ? item?.priority < pricingRules[0].priority : true)
      ) {
        const offerIndex =
          pricingRules[0]?.mPricingXProducts?.length > 0 && pricingRules[0].excludeProducts === "N"
            ? pricingRules[0]?.mPricingXProducts?.findIndex((op) => op.mProductId === item.productId)
            : pricingRules[0]?.mPricingXProducts?.length > 0 && pricingRules[0].excludeProducts === "Y"
              ? pricingRules[0]?.mPricingXProducts?.findIndex((op) => op.mProductId !== item.productId)
              : 0;
        const eligibleAmount =
          Number(pricingRule?.eligibleAmount || 0) > 0
            ? Number(pricingRule.eligibleAmount)
            : hasApplicableProducts
              ? cartObj.items
                  .filter((cartItem) => pricingRule.applicableProducts.some((p) => p?._id === cartItem?.productId))
                  .reduce((sum, cartItem) => sum + Number(cartItem?.linegross || cartItem?.nettotal || 0), 0)
              : Number(cart?.total || 0);

        if (offerIndex >= 0 && Math.abs(item.weight) >= pricingRules[0].minimumQty) {
          if (hasApplicableProducts) {
            const matchedProduct = pricingRule.applicableProducts.find((p) => p?._id === item?.productId);

            const matchedQty = Number(matchedProduct?.quantity);

            eligibleQty = matchedProduct && matchedQty > 0 ? Math.min(Number(item?.weight || 0), matchedQty) : Number(item?.weight || 0);
          }
          if (Array.isArray(item.children) && item.children.length > 0) {
            item.children.forEach((child) => {
              let discount = 0;

              if (pricingRules[0].discountType === "P" && pricingRules[0].percentageDiscount) {
                discount = (pricingRules[0].percentageDiscount / 100) * parseFloat(child.realPrice);
              } else if (pricingRules[0].discountType === "V" && pricingRules[0].amountDiscount) {
                discount = eligibleAmount > 0 ? (pricingRule.amountDiscount / eligibleAmount) * parseFloat(child.realPrice) : 0;
              }

              discount = parseFloat(discount).toFixed(tillData.tillAccess.csBunit.currencies[0].prcPrecision);

              discountAmt += parseFloat((discount * child.weight).toFixed(tillData.tillAccess.csBunit.currencies[0].prcPrecision));
            });
          } else {
            let discount = 0;

            if (pricingRules[0].discountType === "P" && pricingRules[0].percentageDiscount) {
              discount = (pricingRules[0].percentageDiscount / 100) * parseFloat(item.realPrice);
            } else if (pricingRules[0].discountType === "V" && pricingRules[0].amountDiscount) {
              discount = eligibleAmount > 0 ? (pricingRule.amountDiscount / eligibleAmount) * parseFloat(item.realPrice) : 0;
            }

            discount = parseFloat(discount).toFixed(tillData.tillAccess.csBunit.currencies[0].prcPrecision);

            discountAmt += parseFloat((discount * item.weight).toFixed(tillData.tillAccess.csBunit.currencies[0].prcPrecision));
          }
        } else {
          item.discount = 0;
          delete item.nextRule;
        }
      }
    });
    if (pricingRules[0].mPricingApprovals) {
      approvalFlag = await new Promise((resolve) => {
        discountApproval(pricingRules[0].mPricingApprovals, pricingRules[0], couponInput, discountAmt, cart?.totalStd, (eventData) => {
          resolve(eventData?.approvalGranted);
        });
      });
    }

    let discardCoupon = false;
    if (pricingRules && approvalFlag) {
      setCouponModalVisible(false);
      setCouponInput("");
      const pricingRule = pricingRules[0];
      let finalObj;
      if (pricingRule.type === "TD" && pricingRule.iscoupon === "Y") {
        if (dateValidator(pricingRule.startDate, pricingRule.endDate)) {
          if (pricingRule.timeSpecific === "Y") {
            const weekDay = currentDay();
            const pStartTime = pricingRule.starttime.substring(11);
            const pEndTIme = pricingRule.endtime !== null ? pricingRule.endtime.substring(11) : dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss").substring(11);
            const starttime = timeStamp().substring(0, 10) + " " + pStartTime;
            const endtime = timeStamp().substring(0, 10) + " " + pEndTIme;
            if (timeValidator(starttime, endtime) && pricingRule[weekDay] === "Y") {
              finalObj = processBillDiscounts(pricingRule, cart, true, couponInput || couponData?.couponcode, uniqReferenceId, couponData?.mPricingCouponId);
            }
          } else {
            finalObj = processBillDiscounts(pricingRule, cart, true, couponInput || couponData?.couponcode, uniqReferenceId, couponData?.mPricingCouponId);
          }
        }
        if (finalObj) {
          if (finalObj.couponError) {
            message.warning(finalObj.couponError);
            discardCoupon = true;
          } else {
            if (!isExchange) {
              setCart(finalObj);
            }
            let filteredCoupon = finalObj?.couponInput?.findIndex((item) => item.mPricingCouponId === couponData?.mPricingCouponId || item.couponCode === couponData?.couponcode);
            if (filteredCoupon >= 0) {
              message.success(`Coupon Applied: ${pricingRule.printedName}`);
              setAdditionalInfo([]);
              setCouponInput("");
            } else {
              discardCoupon = true;
            }
          }
        } else {
          discardCoupon = true;
        }
      }

      if (pricingRule.type === "TDF" && pricingRule.iscoupon === "Y") {
        if (dateValidator(pricingRule.startDate, pricingRule.endDate)) {
          if (pricingRule.timeSpecific === "Y") {
            const weekDay = currentDay();
            const pStartTime = pricingRule.starttime.substring(11);
            const pEndTIme = pricingRule.endtime !== null ? pricingRule.endtime.substring(11) : dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss").substring(11);
            const starttime = timeStamp().substring(0, 10) + " " + pStartTime;
            const endtime = timeStamp().substring(0, 10) + " " + pEndTIme;
            if (timeValidator(starttime, endtime) && pricingRule[weekDay] === "Y") {
              processBillDiscounts(pricingRule, cart, true, couponInput || couponData?.couponcode, uniqReferenceId, couponData?.mPricingCouponId, couponData?.mPricingRulesId);
            }
          } else {
            processBillDiscounts(pricingRule, cart, true, couponInput || couponData?.couponcode, uniqReferenceId, couponData?.mPricingCouponId, couponData?.mPricingRulesId);
          }
        }
      }

      if (pricingRule.type !== "TDF" && pricingRule.iscoupon === "Y" && pricingRule.type !== "TD") {
        let addToCart = cart.items.filter((item) => pricingRule.mPricingXProducts.some((pro) => item.productId === pro.mProductId));

        if (addToCart.length === 0 && cart.items.length > 0) {
          addToCart = cart.items[cart.items.length - 1];
        } else {
          addToCart = addToCart[0];
        }

        let cartObjLocal = isExchange ? useCartStore.getState().exchangeCart : cart;
        let iscoupon = true;
        let expiryDiscount = useTransactionStore.getState().expiryDiscount === "Y";
        let updatedCart = expiryDiscount
          ? cartObjLocal
          : await pricingRuleController(
              addToCart,
              cartObjLocal,
              cart,
              setCart,
              cartRef,
              orderType,
              iscoupon,
              selectedCoupon?.externalCoupon === "Y" ? selectedCoupon.name : couponInput || couponData?.couponcode,
              uniqReferenceId,
              verifyCoupons.mPricingCouponId,
              verifyCoupons.mPricingRulesId || verifyCoupons.mPricingrulesId,
              undefined,
              pricingRule
            );
        let updatedTotalTax = 0;
        let updatedTotalPrice = 0;
        let updatedTotalItemsQty = 0;
        let updatedTotalDiscounts = 0;
        for (let i = 0; i < updatedCart.items.length; i += 1) {
          updatedTotalPrice += updatedCart.items[i].nettotal;
          updatedTotalItemsQty += updatedCart.items[i].weight;
          updatedTotalTax += updatedCart.items[i].taxAmount;
          updatedTotalDiscounts += updatedCart.items[i].discount;
          updatedCart.items[i].key = i;
        }

        const { roundedTotal: updatedRoundOffValue, roundOffAmount: updatedTotalRoundOff } = applyCurrencyRounding(updatedTotalPrice, getRoundingMethod(tillData));
        const allItemsInCart = (updatedCart.items || []).flatMap((item) => (Array.isArray(item.children) && item.children.length > 0 ? item.children : [item]));
        const updatedTotalStd = allItemsInCart.reduce((sum, item) => sum + (item.linestd || 0), 0);
        const rawCouponInfo = useTransactionStore.getState().couponAdditionalInfo;
        const couponInfo = {};
        if (rawCouponInfo && rawCouponInfo["Discount Reason"]) {
          const camelKey = toCamelCase("Discount Reason");
          couponInfo[camelKey] = rawCouponInfo["Discount Reason"];
        }

        const updatedCouponInput = updatedCart.couponInput.map((item) => {
          if (typeof item !== "object") return item;
          const updated = { ...item, percentageDiscount: pricingRules[0].percentageDiscount };
          if (couponData?.crmDiscountType) {
            updated.discountType = couponData.crmDiscountType;
            updated.percentageDiscount = couponData.crmPercentageDiscount;
            updated.amountDiscount = couponData.crmAmountDiscount;
          }
          return updated;
        });
        // BUG-1880: FlatDiscount/PercentageDiscount/etc. mutate item.nettotal/taxAmount
        // in place but never recompute the tax-breakup grouping, so it kept showing
        // pre-discount amounts even though the cart total reflected the coupon correctly.
        const isInterStateForTax = getIsInterState(updatedCart);
        const groupedItemsByTaxRate = groupItemsByTaxRate(
          updatedCart.items.filter((item) => item.weight !== 0),
          precision,
          { isInterState: isInterStateForTax }
        );
        let finalCartObj = {
          ...updatedCart,
          items: [...updatedCart.items],
          couponInput: updatedCouponInput,
          total: parseFloat(parseFloat(updatedTotalPrice)?.toFixed(precision)),
          tax: parseFloat(parseFloat(updatedTotalTax)?.toFixed(precision)),
          // Some pricing-rule types (e.g. FlatDiscount) only mutate item-level
          // fields and never touch cart-level subTotal, unlike PercentageDiscount
          // which self-corrects it. Recomputing subTotal here from the same
          // summed items keeps it consistent for every coupon type — without
          // this, useAdditionalCharges' TCS/charges recompute effect rebuilds
          // cart.total from the stale pre-discount subTotal and silently
          // reverts the coupon discount (BUG-1667).
          subTotal: parseFloat(parseFloat(updatedTotalPrice - updatedTotalTax)?.toFixed(precision)),
          discount: parseFloat(parseFloat(updatedTotalDiscounts)?.toFixed(precision)),
          totalQty: updatedTotalItemsQty,
          roundOff: parseFloat(parseFloat(updatedTotalRoundOff)?.toFixed(precision)),
          totalStd: updatedTotalStd,
          discountInfo: couponInfo,
          groupedItemsByTaxRate,
        };
        if (isExchange) {
          useCartStore.getState().setExchangeCart(finalCartObj);
        } else {
          setCart(finalCartObj);
        }
        let filteredCoupon = finalCartObj?.couponInput?.length > 0 ? finalCartObj?.couponInput?.findIndex((item) => item.mPricingCouponId === verifyCoupons.mPricingCouponId) : -1;
        if (filteredCoupon >= 0) {
          message.success(`Coupon Applied: ${pricingRule.printedName}`);
        } else {
          discardCoupon = true;
        }
      }

      if (discardCoupon) {
        const serverUrl = process.env.REACT_APP_serverUrl;
        const couponsData = [{ couponCode: couponInput || couponData?.couponcode, referenceId: uniqReferenceId }];
        const discardCoupons = async (coupon) => {
          await discardCouponService(coupon.couponCode, coupon.referenceId);
        };
        couponsData.forEach((coupon) => {
          discardCoupons(coupon);
        });
        if (pricingRule.type === "TD" || pricingRule.type === "TDF") {
          if (!finalObj) return;
          finalObj.totalBillDicount = null;
          setCart(finalObj);
        } else {
          // Line-item coupon types (FD/PD/PA/QD/PK/XGY/BXS/BXD) previously failed here with
          // no feedback at all: the pricing-rule handler found no eligible/unclaimed line for
          // the coupon (e.g. every matching line already carries a higher-priority discount),
          // so it never pushed a couponInput entry, filteredCoupon stayed -1, and the cashier
          // saw neither a success nor a warning message. TSK-2965 / ticket 4812.
          message.warning(`${messages.errors.coupons.discount_conflict}`);
        }
      }
      upsertPOSLog(cart, "CUP");
      setAdditionalInfo([]);
      setCouponModalVisible(false);
      if (searchInputRef?.current) {
        searchInputRef?.current?.focus();
      }
    } else {
      message.warning(`${messages.errors.coupons.not_available}`);
    }
  };

  const couponInfo = async (values) => {
    const formatted = {
      ...values,
      DateOfBirth: values.DateOfBirth?.format("DD-MM-YYYY"),
    };
    useTransactionStore.getState().setCouponAdditionalInfo(formatted);
    await processCouponApplication(couponVerificationData.verifyCoupons, couponVerificationData.pricingRules, couponVerificationData.uniqReferenceId);
    additionalInfoForm.resetFields();
    setShowAdditionalInfo(false);
    setShowCouponApproval(true);
  };

  const checkExternalCoupon = async (coupon) => {
    // Same double-submit guard as checkCoupon: this function is the single point of
    // contact with the external provider (Huntr), so it must not be able to fire a
    // second redemption request while the first one is still in flight.
    if (isValidatingRef.current) return;
    isValidatingRef.current = true;
    setCouponValidating(true);
    try {
      const alreadyApplied = cart?.couponInput?.some((c) => typeof c === "object" && c.mPricingruleId === coupon?.mPricingrulesId);
      if (alreadyApplied) {
        message.warning(`${messages.errors.coupons.multiple_entries}`);
        return;
      }

      const providerConfig = coupon?.couponProvider?.[0]?.apiConfig;
      if (!providerConfig) {
        console.error("Missing coupon provider configuration");
        return;
      }
      const parsedConfig = JSON.parse(providerConfig);

      const url = parsedConfig.api_url;
      const token = parsedConfig.api_key;

      const currPrecision = tillData?.tillAccess?.csBunit?.currencies[0]?.prcPrecision || 2;
      let discountAmount = 0;
      if (selectedCoupon?.type === "TD") {
        discountAmount = totalBillDiscountData?.discountedAmount?.toFixed(currPrecision) || 0;
      } else {
        const discountAmt = selectedCoupon?.discountType === "V" ? (selectedCoupon?.amountDiscount ?? "") : (selectedCoupon?.percentageDiscount ?? "");
        if (cart.items.length > 0) {
          discountAmount =
            selectedCoupon?.discountType === "P"
              ? (cart.items.reduce((total, item) => total + item.realPrice * item.weight, 0) * (discountAmt / 100)).toFixed(
                  tillData?.tillAccess?.csBunit?.currencies[0]?.prcPrecision || 2
                )
              : discountAmt;
        }
      }

      const userId = couponInput?.includes("HTR?") ? couponInput.split("HTR?")[1] : couponInput;
      const body = {
        perk: 9,
        billAmount: cart.total,
        savings: discountAmount,
        userId: userId,
        type: "perk",
      };

      const result = await externalCoupon(url, token, body);
      if (result && result.error === false) {
        let uniqReferenceId = uuidv4().replace(/-/g, "").toUpperCase();
        const matchingPricingRules = findPricingRuleById(selectedCoupon.mPricingrulesId);
        const pricingRuleForCoupon = matchingPricingRules.length > 0 ? matchingPricingRules[0] : selectedCoupon;
        await processCouponApplication(selectedCoupon, pricingRuleForCoupon, uniqReferenceId);
      } else if (result?.error === true) {
        message.warning(result?.data?.message);
        setCouponInput("");
        couponInputRef.current?.focus();
      }
    } finally {
      isValidatingRef.current = false;
      setCouponValidating(false);
    }
  };

  const checkCoupon = async () => {
    if (isValidatingRef.current) return;
    isValidatingRef.current = true;
    setCouponValidating(true);
    try {
    if (navigator.onLine) {
      const authHeaders = getOAuthHeaders();
      let setAuthTokens;
      if (authHeaders && authHeaders.access_token) {
        setAuthTokens = authHeaders.access_token;
      }
      let uniqReferenceId = uuidv4().replace(/-/g, "").toUpperCase();
      let couponFlag = true;
      cart?.couponInput?.forEach((coupon) => {
        if (coupon.couponCode === couponInput) {
          couponFlag = false;
        }
        // For external coupons the cart stores selectedCoupon.name as couponCode (not the
        // user's typed input), so the code-equality check above never matches.  Guard by
        // pricing-rule ID so the same external provider cannot be applied twice.
        if (selectedCoupon?.externalCoupon === "Y" && coupon.mPricingruleId === selectedCoupon?.mPricingrulesId) {
          couponFlag = false;
        }
      });
      const mobileNo = cart?.customer?.mobileNo ? cart.customer.mobileNo : null;
      // DI-30: Log coupon validation with terminal context for race-condition audit
      logCouponValidation({
        couponCode: couponInput,
        referenceId: uniqReferenceId,
        tillId: tillData?.tillAccess?.cwrTill?.cwrTillID || null,
        bunitId: tillData?.tillAccess?.csBunit?.csBunitId || null,
      });
      const currPrecision = tillData?.tillAccess?.csBunit?.currencies[0]?.prcPrecision || 2;
      let discountAmount = 0;
      if (selectedCoupon?.type === "TD") {
        discountAmount = totalBillDiscountData?.discountedAmount?.toFixed(currPrecision) || 0;
      } else {
        const discountAmt = selectedCoupon?.discountType === "V" ? (selectedCoupon?.amountDiscount ?? "") : (selectedCoupon?.percentageDiscount ?? "");
        if (cart.items.length > 0) {
          discountAmount =
            selectedCoupon?.discountType === "P"
              ? (cart.items.reduce((total, item) => total + item.realPrice * item.weight, 0) * (discountAmt / 100)).toFixed(
                  tillData?.tillAccess?.csBunit?.currencies[0]?.prcPrecision || 2
                )
              : discountAmt;
        }
      }
      const finalCouponInput = selectedCoupon?.couponProvider?.[0]?.providerCode === "HUNTR" ? couponInput?.replace(/^HTR\?/, "") : couponInput;
      if (couponFlag) {
        try {
          const data = {
            query: `query{
             verifyCoupons(couponcode:"${finalCouponInput}",referenceId: "${uniqReferenceId}",bunitId: "${tillData.tillAccess.csBunit.csBunitId}", contactNo: ${mobileNo ? `"${mobileNo}"` : null}, pricingRuleId: ${selectedCoupon?.mPricingrulesId ? `"${selectedCoupon?.mPricingrulesId}"` : null}, billAmount: ${cart.total ? `"${cart.total}"` : null}, savings: ${discountAmount ? `"${discountAmount}"` : null}){
              mPricingCouponId
              csClientId
              csBunitId
              created
              createdBy
              updated
              updatedBy
              line
              mPricingRulesId
              couponcode
              status
              redemptionCount
              usedDate
              upc
              otp
              message
              additionalInfo {
                fieldName
                fieldType
                mandatory
                sequence
                displayLabel
                validationRule
                }
          }
      }
        `,
          };

          const verifyCouponResponse = await verifyCouponsService(data);

          const verifyCouponsResult = verifyCouponResponse?.data?.data?.verifyCoupons || [];
          if (verifyCouponsResult?.length > 0) {
            if (verifyCouponsResult[0].status === "A") {
              const matchingPricingRules = findPricingRuleById(verifyCouponsResult[0].mPricingRulesId);
              setCouponVerificationData({
                verifyCoupons: verifyCouponsResult[0],
                pricingRules: matchingPricingRules[0],
                uniqReferenceId,
              });

              if (verifyCouponsResult[0]?.additionalInfo?.length > 0) {
                setAdditionalInfo(verifyCouponsResult[0]?.additionalInfo);
                return;
              }
              await processCouponApplication(verifyCouponsResult[0], matchingPricingRules, uniqReferenceId);
            } else if (verifyCouponsResult[0].status === "201") {
              message.warning(verifyCouponsResult[0].message);
            } else {
              message.warning(`${messages.errors.coupons.coupon_expired}`);
            }
          } else {
            Sentry.captureException(new Error("Coupon code failed"), {
              extra: {
                couponcode: couponInput,
                referenceId: uniqReferenceId,
              },
            });
            message.warning(`${t("coupon_error")}`);
          }
        } catch (error) {
        }
      } else {
        message.warning(`${messages.errors.coupons.multiple_entries}`);
      }
      setIsInputFocused(false);
    } else {
      Modal.error({
        title: "Sync Failed",
        content: "Please check your network connection and try again",
      });
    }
    } finally {
      isValidatingRef.current = false;
      setCouponValidating(false);
    }
  };

  const removeCoupon = async (coupon) => {
    const authHeaders = getOAuthHeaders();
    let setAuthTokens;
    if (authHeaders && authHeaders.access_token) {
      setAuthTokens = authHeaders.access_token;
    }
    const discardCouponFn = async () => {
      try {
        const response = await discardCouponService(coupon.couponCode, coupon.referenceId);
      } catch (error) {
        console.error(`Error discarding coupon ${coupon.couponCode}:`, error);
      }
    };
    discardCouponFn();
    let latestData = await removeAllDiscounts();

    let cartObjLocal = {
      ...latestData,
      // removeAllDiscounts() already carries the cart's existing customer through via spreads —
      // only fall back to the sale-type/default customer if the cart genuinely has none.
      customer: latestData?.customer ? latestData.customer : selectedSaleTypeData?.customer?.b2cCustomerId ? selectedSaleTypeData?.customer : defaultCustomer,
    };
    cartObjLocal.couponInput = cartObjLocal.couponInput.filter((couponCode) => couponCode.mPricingCouponId !== coupon.mPricingCouponId);
    // The coupon being removed is the one that set totalBillDicount (bill-level TD/TDF) —
    // clear it here, mirroring the existing discard-on-validation-failure handling above.
    // Scoped to this specific coupon's id (not "any coupons left") so removing a bill-level
    // coupon while a separate line-level coupon remains still clears the stale flag, and
    // vice versa a remaining bill-level coupon is left untouched.
    if (cartObjLocal.totalBillDicount && cartObjLocal.totalBillDicount === coupon.mPricingruleId) {
      cartObjLocal.totalBillDicount = null;
      cartObjLocal.iscoupon = false;
    }
    let updatedCart = cartObjLocal;
    let cartLocal = cartObjLocal;

    if (cartObjLocal.items?.length > 0) {
      await Promise.all(
        cartObjLocal.items.map(async (ele) => {
          let addToCart = ele;
          addToCart.nettotal = parseFloat(parseFloat(addToCart.nettotal)?.toFixed(precision));
          updatedCart = await pricingRuleController(addToCart, cartObjLocal, cartObjLocal, setCart, cartObjLocal, orderType);
          return updatedCart;
        })
      );
    }
    if (cartObjLocal.totalDiscountFlag) {
      await openPaymentModalByCustomer(updatedCart);
    }

    if (updatedCart?.couponInput?.length > 0) {
      let addToCart = cartObjLocal.items[0];
      await Promise.all(
        updatedCart.couponInput.map(async (couponItem) => {
          const matchingPricingRules = findPricingRuleById(couponItem.mPricingruleId);
          if (matchingPricingRules[0].type !== "TD" && matchingPricingRules[0].type !== "TDF") {
            updatedCart = await pricingRuleController(
              addToCart,
              updatedCart,
              cartLocal,
              setCart,
              cartRef,
              orderType,
              true,
              couponItem.couponCode,
              couponItem.referenceId,
              couponItem.mPricingCouponId,
              couponItem.mPricingruleId
            );
          } else {
            updatedCart = await processBillDiscounts(
              matchingPricingRules[0],
              updatedCart,
              true,
              couponItem.couponCode,
              couponItem.referenceId,
              couponItem.mPricingCouponId,
              couponItem.mPricingruleId
            );
          }
        })
      );
    }

    let updatedTotalTax = 0;
    let updatedTotalPrice = 0;
    let updatedTotalItemsQty = 0;
    let updatedTotalDiscounts = 0;

    updatedCart.items = updatedCart.items.map((item, i) => {
      const nettotalFixed = parseFloat(parseFloat(item.nettotal)?.toFixed(precision));
      const taxAmountFixed = parseFloat(parseFloat(item.taxAmount)?.toFixed(precision));
      const discountFixed = item.discount ? parseFloat(parseFloat(item.discount)?.toFixed(precision)) : 0;

      updatedTotalPrice += nettotalFixed;
      updatedTotalItemsQty += item.weight;
      updatedTotalTax += taxAmountFixed;
      updatedTotalDiscounts += discountFixed;

      item.discount = discountFixed;
      item.key = i;
      item.nettotal = parseFloat(parseFloat(item.nettotal)?.toFixed(precision));

      if (!item.isGiftCard) {
        let lineTax = item.listPrice * (1 + item.taxRate / 100);
        let unitPrice = isFinite(item.nettotal / item.weight) ? (taxIncludeFlag === "Y" ? item.nettotal / item.weight / (1 + item.taxRate / 100) : item.salePrice) : 0;
        if (!isFinite(unitPrice)) unitPrice = 0;

        let unitTax = item.taxAmount / item.weight;
        const stdTax = item.sunitprice * (1 + item.taxRate / 100);
        item.sunitprice = taxIncludeFlag === "Y" ? item.sunitprice : parseFloat(parseFloat(item.sunitprice + unitTax)?.toFixed(precision));
        const gross_list = taxIncludeFlag === "Y" ? item.listPrice : item.listPrice * (1 + item.taxRate / 100);
        const grossUnit = Math.abs(item.sunitprice) - Math.abs(item.discount) / Math.abs(item.weight);
        const netList =
          taxIncludeFlag === "Y" ? item.listPrice - parseFloat(parseFloat(item.listPrice - item.listPrice / (1 + item.taxRate / 100))?.toFixed(precision)) : item.listPrice;
        const grossStd = taxIncludeFlag === "Y" ? item.sunitprice : parseFloat(parseFloat(stdTax)?.toFixed(precision));

        item.listPrice = taxIncludeFlag === "Y" ? item.listPrice : parseFloat(parseFloat(lineTax)?.toFixed(precision));
        item.linetax = Math.abs(nettotalFixed) > 0 ? taxAmountFixed : 0;
        item.linenet = Math.abs(nettotalFixed) > 0 ? parseFloat(parseFloat(item.nettotal - item.taxAmount)?.toFixed(precision)) : 0;
        item.linegross = Math.abs(nettotalFixed) > 0 ? nettotalFixed : 0;
        item.linestd = Math.abs(nettotalFixed) > 0 ? parseFloat(parseFloat(grossStd * item.weight)?.toFixed(precision)) : 0;
        item.netunit = Math.abs(nettotalFixed) > 0 ? parseFloat(parseFloat(unitPrice)?.toFixed(precision)) : 0;
        item.listprice = Math.abs(nettotalFixed) > 0 ? item.listPrice : 0;
        item.grossunit = Math.abs(nettotalFixed) > 0 ? parseFloat(parseFloat(unitPrice)?.toFixed(precision)) * (item.isReturn ? -1 : 1) : 0;
        item.grossstd = Math.abs(nettotalFixed) > 0 ? roundTo(grossStd, precision) : 0;
        item.grosslist = Math.abs(nettotalFixed) > 0 ? roundTo(gross_list, precision) : 0;
        item.netList = Math.abs(nettotalFixed) > 0 ? netList : 0;
        item.unitPrice = Math.abs(nettotalFixed) > 0 ? unitPrice : 0;
        item.taxAmount = Math.abs(nettotalFixed) > 0 ? taxAmountFixed : 0;
      }
      return item;
    });

    const sumLineTotals = (lines) => {
      let totalLineGross = 0;
      let totalLineTax = 0;

      lines.forEach((line) => {
        totalLineGross += line.nettotal;
        totalLineTax += line.taxAmount;
      });

      return { totalLineGross, totalLineTax };
    };
    const adjustOrderTotals = (order, lines) => {
      const { totalLineGross, totalLineTax } = sumLineTotals(lines);
      let adjustedOrderGross = order.total;
      let adjustedOrderTax = order.tax;

      const grossDifference = totalLineGross - adjustedOrderGross;
      const taxDifference = totalLineTax - adjustedOrderTax;

      if (Math.abs(grossDifference) > 0.01) {
        adjustedOrderGross += grossDifference;
      }

      if (Math.abs(taxDifference) > 0.01) {
        adjustedOrderTax += taxDifference;
      }

      return {
        ...order,
        total: adjustedOrderGross,
        tax: adjustedOrderTax,
      };
    };
    updatedCart = adjustOrderTotals(updatedCart, updatedCart.items);

    // BUG-1880: same tax-breakup staleness gap as processCouponApplication above —
    // removing a coupon restores item.nettotal/taxAmount but the tax breakup grouping
    // was never rebuilt from the restored values.
    const isInterStateForTaxRemove = getIsInterState(updatedCart);
    const groupedItemsByTaxRateRemove = groupItemsByTaxRate(
      updatedCart.items.filter((item) => item.weight !== 0),
      precision,
      { isInterState: isInterStateForTaxRemove }
    );

    let finalCartObj = {
      ...updatedCart,
      items: [...updatedCart.items],
      discount: updatedTotalDiscounts,
      totalQty: updatedTotalItemsQty,
      discountInfo: [],
      groupedItemsByTaxRate: groupedItemsByTaxRateRemove,
    };
    if (!isExchange) {
      setCart({ ...finalCartObj });
    }
    useTransactionStore.getState().setCouponAdditionalInfo(null);
    setSelectedCoupon(null);
  };

  return {
    // State
    couponModalVisible,
    setCouponModalVisible,
    couponInput,
    setCouponInput,
    couponVerificationData,
    setCouponVerificationData,
    showCouponApproval,
    setShowCouponApproval,
    couponApprovals,
    setCouponApprovals,
    selectedCoupon,
    setSelectedCoupon,
    totalBillCouponData,
    setTotalBillCouponData,
    showAdditionalInfo,
    setShowAdditionalInfo,
    additionalInfo,
    setAdditionalInfo,
    couponInputRef,
    couponValidating,
    // Handlers
    closeCouponModal,
    handleCouponInput,
    processCouponApplication,
    couponInfo,
    checkExternalCoupon,
    checkCoupon,
    removeCoupon,
  };
};

export default useCouponProcessing;
